
import React, { useEffect } from 'react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Building, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { checkSubdomainAvailability } from '@/utils/instanceService';

// Form schema for the business details step
export const businessDetailsSchema = z.object({
  companyName: z.string().min(2, { message: "Company name must be at least 2 characters" }),
  subdomain: z.string().min(3, { message: "Subdomain must be at least 3 characters" })
    .regex(/^[a-z0-9-]+$/, { message: "Subdomain can only contain lowercase letters, numbers, and hyphens" }),
  primaryColor: z.string().regex(/^#([A-Fa-f0-9]{6})$/, { message: "Please enter a valid hex color code" })
});

export type BusinessDetailsFormValues = z.infer<typeof businessDetailsSchema>;

interface BusinessDetailsFormProps {
  form: UseFormReturn<BusinessDetailsFormValues>;
  onNextStep: () => void;
  subdomainAvailable: boolean | null;
  subdomain: string;
  isLoading: boolean;
}

const BusinessDetailsForm: React.FC<BusinessDetailsFormProps> = ({ 
  form, 
  onNextStep, 
  subdomainAvailable, 
  subdomain,
  isLoading
}) => {
  return (
    <Form {...form}>
      <form className="space-y-6">
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input 
                    className="pl-10" 
                    placeholder="Acme Corporation" 
                    {...field} 
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="subdomain"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subdomain</FormLabel>
              <FormControl>
                <div className="flex items-center">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input 
                      className="pl-10" 
                      placeholder="yourcompany" 
                      {...field} 
                    />
                  </div>
                  <span className="ml-2 text-gray-500">.referme.site</span>
                </div>
              </FormControl>
              {subdomain && subdomain.length >= 3 && (
                <div className="mt-2">
                  {subdomainAvailable === null ? (
                    <p className="text-sm text-gray-500">Checking availability...</p>
                  ) : subdomainAvailable ? (
                    <p className="text-sm text-green-600 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" /> Subdomain is available
                    </p>
                  ) : (
                    <p className="text-sm text-red-600">This subdomain is already taken</p>
                  )}
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="primaryColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Brand Color</FormLabel>
              <FormControl>
                <div className="flex items-center space-x-2">
                  <Input 
                    type="text" 
                    placeholder="#4F46E5" 
                    {...field} 
                  />
                  <div 
                    className="h-10 w-10 rounded border border-gray-300"
                    style={{ backgroundColor: field.value }}
                  ></div>
                </div>
              </FormControl>
              <p className="text-sm text-gray-500 mt-1">Hex color code (e.g. #4F46E5)</p>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button
          type="button"
          variant="arrow"
          className="w-full"
          size="lg"
          onClick={onNextStep}
          disabled={isLoading || !form.formState.isValid || !subdomainAvailable}
        >
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </form>
    </Form>
  );
};

export default BusinessDetailsForm;
